import { formatDateTime, formatTime } from './format'

/**
 * The console's clock and language.
 *
 * The staff console is the boutique's own tool, not a customer surface. It was
 * reading `navigator.language` and the browser's timezone, which meant it
 * rendered whatever the person looking happened to be set to: opened from
 * Taipei it printed `2026年9月`, `週日` and `晚上10:00` in an interface that is
 * otherwise entirely in English, and it told the truth about the clock on the
 * wrong wall — an appointment at 10am in the showroom read as 1am.
 *
 * So both are pinned to the shop. English because that is the language the
 * console is written in, and Los Angeles because that is where the showroom
 * is; `formatDay` stays UTC because a bare `YYYY-MM-DD` is a calendar day
 * rather than an instant, and shifting it would move the date she typed.
 *
 * The timezone is duplicated from the tenant record on purpose — the console
 * has no endpoint that carries it, and inventing one for a single-boutique
 * tool would be more machinery than the fact deserves. `boutique.test.ts`
 * fails if the two ever disagree, so the copy cannot rot quietly. The day a
 * second boutique gets a console, this comes down the wire and this module
 * becomes a hook.
 */
export const BOUTIQUE_LOCALE = 'en-US'
export const BOUTIQUE_TIME_ZONE = 'America/Los_Angeles'

/** A time of day, on the showroom's clock. */
export function boutiqueTime(iso: string): string {
  return formatTime(iso, BOUTIQUE_LOCALE, BOUTIQUE_TIME_ZONE)
}

/** A date and time, on the showroom's clock. */
export function boutiqueDateTime(iso: string): string {
  return formatDateTime(iso, BOUTIQUE_LOCALE, BOUTIQUE_TIME_ZONE)
}

/**
 * Which calendar day an instant falls on in the showroom — the square an
 * appointment is drawn in, and never the viewer's own day. `en-CA` is asked
 * for because its short date is already `YYYY-MM-DD`; the parts are
 * reassembled by name rather than trusted in order.
 */
export function boutiqueDayOf(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso.slice(0, 10)
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BOUTIQUE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const read = (type: string): string => parts.find((p) => p.type === type)?.value ?? ''
  return `${read('year')}-${read('month')}-${read('day')}`
}

/**
 * Today in the showroom, which is the day the console opens on.
 *
 * Not the viewer's today: a stylist checking the book from anywhere east of
 * Los Angeles after 4pm local would otherwise land on tomorrow's empty page.
 */
export function boutiqueToday(): string {
  return boutiqueDayOf(new Date().toISOString())
}

/**
 * The showroom's wall clock at an instant, `HH:MM` 24-hour — what the
 * reschedule form starts from and the API takes back. Paired with
 * `boutiqueDayOf`, the two halves name the instant in the shop's own words,
 * which is how the API is spoken to: it resolves them in the tenant's zone.
 */
export function boutiqueWallTime(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BOUTIQUE_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const read = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00'
  return `${read('hour')}:${read('minute')}`
}
